//#region Asset Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import { dxFormSimpleItem } from 'devextreme/ui/form'
import { dxNumberBoxOptions } from 'devextreme/ui/number_box'

//#endregion

import { ConvertHelper, IUserSession, ToastHelper, LocalizationHelper, LogHelper, TemplateHelper, ConfigHelper } from '../../../../helpers'
import { OpLocation } from '../../../../models/core'
import { IMRConfirmationPopupOptions, IMRConfirmationPopupInitializedArgs, IIMRConfirmationPopup } from '../../../common/confirmation-popup2'



// Public Types

export interface IIMRCashierPanelNewPaymentPopup {
    setConsumerLocation(location: OpLocation): void
    togglePopup(): void
}

export type IMRCashierPanelNewPaymentPopupOptions = {
    onInitialized?(locationDetails: IIMRCashierPanelNewPaymentPopup): void
    onPaymentDone?(): void
    paymentMode?: IMRCashierPanelPaymentMode
}

export enum IMRCashierPanelPaymentMode {
    NewPayment = 0,
    UpdateOwed = 1
}

// Public Types End


export class IMRCashierPanelNewPaymentPopupController implements ng.IComponentController, IIMRCashierPanelNewPaymentPopup {

    // Consts

    private static get LOG_PREFIX(): string { return '[SIMAX.Module.CashierPanel.NewPaymentPopup]' }

    // Consts End

    // Members

    private isLoading: boolean
    private options: IMRCashierPanelNewPaymentPopupOptions

    private consumerLocation: OpLocation;
    private currencyString: string

    private popup: DevExpress.ui.dxPopup
    private popupOptions: DevExpress.ui.dxPopupOptions

    private form: DevExpress.ui.dxForm
    private formOptions: DevExpress.ui.dxFormOptions

    private bottomToolbarOptions: DevExpress.ui.dxToolbarOptions

    private submitButton: DevExpress.ui.dxButton

    private paymentMode: IMRCashierPanelPaymentMode

    public confirmationPopupOptions: IMRConfirmationPopupOptions
    private confirmationPopup: IIMRConfirmationPopup

    // Members End


    // Ctor

    constructor(private userSession: IUserSession) { }

    // Ctor End


    // IIMRCashierPanelNewPaymentPopup Implementation

    public setConsumerLocation(consumerLocation: OpLocation): void {
        this.consumerLocation = consumerLocation
        this.updateCurrency()
    }

    public togglePopup(): void {
        if (this.popup != null) {
            this.popup.toggle(!this.popup.option('visible'))
        }
    }

    // IIMRCashierPanelNewPaymentPopup Implementation End


    // IComponentController Implementation 

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        this.initialize()
    }

    // IComponentController Implementation End


    // Component

    private initialize(): void {
        this.setDefaults()
        this.initializePopup()
        this.initializeForm()
        this.initializeToolbar()
        this.initializeConfirmationPopup()
        this.onInitialized()
    }

    private submitPayment(): void {
        if (this.form.validate() == null || (this.form.validate() as any).isValid) {
            if (this.paymentMode === IMRCashierPanelPaymentMode.NewPayment) {
                this.submitNewPayment()
            } else if (this.paymentMode === IMRCashierPanelPaymentMode.UpdateOwed) {
                this.showConfirmationPopup()
            }
        }
    }

    private submitNewPayment(): void {
        this.toggleSubmitButton()
        const amount: number = Number((this.form.getEditor('amount') as any).option('value'))

        this.userSession
            .getDataProvider().Consumer
            .consumerNewPayment(this.consumerLocation.idLocation, amount)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.PaymentDone))

                this.onPaymentDone()
                this.togglePopup()
            })
            .fail((error: any) => {
                const errorMessage: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMessage || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(IMRCashierPanelNewPaymentPopupController.LOG_PREFIX, `Error during payment operation.`, error)
            })
            .always(() => {
                this.toggleSubmitButton()
            })
    }

    private submitUpdateOwed(): void {
        this.toggleSubmitButton()
        const amount: number = Number((this.form.getEditor('amount') as any).option('value'))

        this.userSession
            .getDataProvider().Consumer
            .consumerUpdateOwed(this.consumerLocation.idConsumer, amount)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.OwedUpdated))

                this.onPaymentDone()
                this.togglePopup()
            })
            .fail((error: any) => {
                const errorMessage: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMessage || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(IMRCashierPanelNewPaymentPopupController.LOG_PREFIX, `Error during payment operation.`, error)
            })
            .always(() => {
                this.toggleSubmitButton()
            })

    }

    // Component End


    // Events

    private onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }

    private onPaymentDone(): void {
        if (this.options != null && _.isFunction(this.options.onPaymentDone)) {
            this.options.onPaymentDone()
        }
    }

    // Events End


    // Popup

    private initializePopup(): void {
        let title: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NewPayment)

        if (this.paymentMode === IMRCashierPanelPaymentMode.UpdateOwed) {
            title = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.UpdateOwed)
        }

        this.popupOptions = {
            height: 'auto',
            title: title,
            width: 'auto',

            onInitialized: (e: any) => this.popup = e.component,
            onShowing: () => {
                if (this.form != null) {
                    this.form.resetValues()
                }
            },
        }
    }

    // Popup


    // Form

    private initializeForm(): void {
        this.formOptions = {
            items: [
                {
                    dataField: 'amount',
                    editorType: 'dxNumberBox',
                    editorOptions: <dxNumberBoxOptions>{
                        format: {
                            precision: 2,
                            type: 'fixedPoint',
                        },
                        placeholder: '0.00',
                        mode: 'number',
                    },

                    label: {
                        text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CreditAmount),
                    },

                    validationRules: [
                        {
                            type: "required",
                            message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CreditAmountIsRequired)
                        },
                        {
                            type: 'custom',
                            validationCallback: (e: any) => e.value > 0 || e.value < 0,
                            message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.InvalidCurrencyAmount)
                        },
                        // {
                        //     type: 'pattern',
                        //     pattern: "^-?[0-9]+(\,[0-9]{2})?$",
                        //     message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.InvalidCurrencyFormat)
                        // },
                    ],
                } as dxFormSimpleItem
            ],

            onInitialized: (e: any) => {
                this.form = e.component
            }
        }
    }

    // Form End


    // Currency

    private updateCurrency(): void {
        if (this.consumerLocation != null &&
            this.consumerLocation.consumer != null &&
            this.consumerLocation.consumer.creditInfo != null &&
            this.consumerLocation.consumer.creditInfo.currency != null
        ) {
            this.currencyString = this.consumerLocation.consumer.creditInfo.currency.toString()
        }
        else {
            this.currencyString = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NotAvailableAbbr)
        }
    }

    // Currentcy 


    // Toolbar

    private initializeToolbar(): void {
        let template: string = TemplateHelper.getIconWithText(
            LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BookPayment),
            ConfigHelper.Icon.ADD,
            this.userSession
        )

        if (this.paymentMode === IMRCashierPanelPaymentMode.UpdateOwed) {
            template = TemplateHelper.getIconWithText(
                LocalizationHelper.getLocalizedText(LocalizationHelper.Text.UpdateOwed),
                ConfigHelper.Icon.DOLLAR,
                this.userSession
            )
        }
        this.bottomToolbarOptions = {
            items: [
                {
                    location: 'after',
                    widget: 'dxButton',
                    options: {
                        template: template,
                        onInitialized: (e: any) => {
                            this.submitButton = e.component
                        },
                        onClick: () => this.submitPayment()
                    },
                },
                {
                    location: 'after',
                    widget: 'dxButton',
                    options: {
                        text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),

                        onClick: () => this.togglePopup()
                    },
                }
            ]
        }
    }

    private toggleSubmitButton(): void {
        const toggle: boolean = !this.submitButton.option('disabled')
        this.submitButton.option('disabled', toggle)
    }

    // Toolbar End

    //#region Confirmation popup

    private initializeConfirmationPopup(): void {
        this.confirmationPopupOptions = {
            onInitialized: (e: IMRConfirmationPopupInitializedArgs) => {
                this.confirmationPopup = e.component
            }
        }
    }

    private showConfirmationPopup(): void {
        this.confirmationPopup
            .show({
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Confirm),
                content: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TopUpRollbackQueston),

                onConfirmed: () => {
                    this.submitUpdateOwed()
                }
            })
    }

    //#endregion


    // Options

    private setDefaults(): void {
        if (this.options != null && this.options.paymentMode != null) {
            this.paymentMode = this.options.paymentMode
        } else {
            this.paymentMode = IMRCashierPanelPaymentMode.NewPayment
        }

        this.currencyString = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NotAvailableAbbr)
    }

    // Options End
}
